/**
 * Team Member DTOs
 * For managing internal team access to admin portal
 */

import { z } from 'zod';
import {
  getPermissionsForRole,
  hasPermission,
  PortalPermission,
  PortalRole,
  ROLE_PERMISSIONS,
} from '../enums/portal.enums';
import {
  BusinessTeamRole,
  BusinessTeamRoleSchema,
  BusinessTeamStatus,
  BusinessTeamStatusSchema,
} from '../enums/team.enums';

// Re-export for backward compatibility
export {
  BusinessTeamRole,
  BusinessTeamRoleSchema,
  BusinessTeamStatus,
  BusinessTeamStatusSchema,
  getPermissionsForRole,
  hasPermission,
  PortalPermission,
  PortalRole,
  ROLE_PERMISSIONS,
};

// ========================================
// DTOs
// ========================================

/**
 * Team member DTO (response)
 */
export const TeamMemberDtoSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  role: z.nativeEnum(PortalRole),
  permissions: z.array(z.nativeEnum(PortalPermission)),
  isActive: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type TeamMemberDto = z.infer<typeof TeamMemberDtoSchema>;

/**
 * Create team member DTO
 */
export const CreateTeamMemberDtoSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(255),
  role: z.nativeEnum(PortalRole),
  password: z.string().min(8).max(255).optional(), // Optional for invite flow
  customPermissions: z.array(z.nativeEnum(PortalPermission)).optional(), // Override role permissions
});

export type CreateTeamMemberDto = z.infer<typeof CreateTeamMemberDtoSchema>;

/**
 * Update team member DTO
 */
export const UpdateTeamMemberDtoSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  role: z.nativeEnum(PortalRole).optional(),
  isActive: z.boolean().optional(),
  customPermissions: z.array(z.nativeEnum(PortalPermission)).optional(),
});

export type UpdateTeamMemberDto = z.infer<typeof UpdateTeamMemberDtoSchema>;

/**
 * Team member query params
 */
export const TeamMemberQueryDtoSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  search: z.string().optional(),
  role: z.nativeEnum(PortalRole).optional(),
  isActive: z.coerce.boolean().optional(),
  sortBy: z.enum(['name', 'email', 'role', 'createdAt', 'lastLoginAt']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type TeamMemberQueryDto = z.infer<typeof TeamMemberQueryDtoSchema>;

/**
 * Paginated team members response
 */
export const PaginatedTeamMembersDtoSchema = z.object({
  data: z.array(TeamMemberDtoSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
  }),
});

export type PaginatedTeamMembersDto = z.infer<typeof PaginatedTeamMembersDtoSchema>;

// ========================================
// Business Portal Team Member
// ========================================

/**
 * Business team member DTO (for business portal)
 * Simpler than admin portal TeamMemberDto
 */
export const BusinessTeamMemberSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  role: BusinessTeamRoleSchema,
  status: BusinessTeamStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});

export type BusinessTeamMember = z.infer<typeof BusinessTeamMemberSchema>;
